import { css } from "@emotion/css"
import { useContext, useState } from "react"
import { z } from "zod"

import Spacer from "@p-ban/common/src/components/layout/Spacer"
import lookup from "@p-ban/common/src/constants/lookup"
import { userValidator } from "@p-ban/common/src/logic/validation/userValidator"
import { refinements, zodResolver, validator } from "@p-ban/common/src/logic/validation/validationScheme"
import type { ValueOf } from "@p-ban/common/src/types"
import { toHankakuNumber, toKatakana } from "@p-ban/common/src/utils/StringUtils"
import COGNITIVE_MEDIA from "@p-ban/core/src/seminar/constant/COGNITIVE_MEDIA"
import PARTICIPATE_INDIVIDUAL_CONSULTATIONS from "@p-ban/core/src/seminar/constant/PARTICIPATE_INDIVIDUAL_CONSULTATIONS"
import SEMINAR_CATEGORY from "@p-ban/core/src/seminar/constant/SEMINAR_CATEGORY"
import {
  holdIndividualConsultations,
  selectableCognitiveMedia,
} from "@p-ban/core/src/seminar/logic/SeminarCategoryLogic"
import USER_TYPE from "@p-ban/core/src/user/constant/USER_TYPE"

import Button from "../../../components/mypage/Button/Button"
import MyPageForm from "../../../components/mypage/MyPageForm"
import MyPageFormField from "../../../components/mypage/MyPageFormField"
import InputLabel from "../../../components/mypage/form/InputLabel"
import CheckBox from "../../../components/mypage/form/checkbox/CheckBox"
import TextArea from "../../../components/mypage/form/input/TextArea"
import TextInput from "../../../components/mypage/form/input/TextInput"
import Radio from "../../../components/mypage/form/radio/Radio"
import RadioWithTextInput from "../../../components/mypage/form/radio/RadioWithTextInput"
import MyPageButtonContainer from "../../../components/mypage/layout/MyPageButtonContainer"
import SimpleGrid from "../../../components/mypage/layout/SimpleGrid"
import useForm from "../../../hooks/useForm"
import MyPageLayout from "../../../layout/MyPage/MyPageLayout"
import useNextRouter from "../../../routes/useNextRouter"
import ConfirmPrivacyPolicy from "../components/ConfirmPrivacyPolicy"
import SeminarSummary from "../components/SeminarSummary"
import SeminarTemplate from "../components/SeminarTemplate"
import REQUEST_CONTENTS from "../constant/REQUEST_CONTENTS"
import { SeminarEntryFormContext } from "../context/SeminarEntryFormContext"

const useSeminarEntryForm = () => {
  const { seminar, seminarEntryForm, setSeminarEntryForm, isLoggedIn } = useContext(SeminarEntryFormContext)

  const { setFieldValue, getFieldProps, getFieldErrors, preSubmit } = useForm(seminarEntryForm, setSeminarEntryForm, {
    correctors: {
      familyNameKana: toKatakana,
      firstNameKana: toKatakana,
      telNo: toHankakuNumber,
    },
    validators: {
      personal: validator.required,
      familyName: userValidator.familyName,
      firstName: userValidator.firstName,
      familyNameKana: userValidator.familyNameKana,
      firstNameKana: userValidator.firstNameKana,
      company: (seminarEntryForm.personal ? lookup(USER_TYPE, seminarEntryForm.personal).isCorporate : false)
        ? userValidator.corporateName
        : zodResolver<string>(
            z
              .string()
              .max(25)
              .superRefine(refinements.noHalfWidthKatakana)
              .superRefine(refinements.noPlatformDependentChar)
          ),
      department: userValidator.departmentName,
      email: userValidator.email,
      telNo: userValidator.phoneNumber,
      requestContents:
        seminar.seminarCategory === SEMINAR_CATEGORY.IntroductionSeminar.value
          ? (requestContents) => {
              return requestContents.length === 0 && seminarEntryForm.requestMessage.trim() === ""
                ? ["必須項目です。"]
                : undefined
            }
          : undefined,

      cognitiveMedia: validator.required,
      cognitiveMediaOther: seminarEntryForm.cognitiveMedia
        ? COGNITIVE_MEDIA[seminarEntryForm.cognitiveMedia].isOther
          ? validator.required
          : undefined
        : undefined,
      participateIndividualConsultations: holdIndividualConsultations(seminar.seminarCategory, seminar.onlineSeminar)
        ? validator.required
        : undefined,
    },
  })

  const usePrivacyPolicy = () => {
    const [isAgreedPrivacyPolicy, setIsAgreedPrivacyPolicy] = useState(false)

    return {
      privacyPolicyProps: {
        checked: isAgreedPrivacyPolicy,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => setIsAgreedPrivacyPolicy(e.target.checked),
      },
    }
  }

  const { privacyPolicyProps } = usePrivacyPolicy()

  const router = useNextRouter("/seminar/entry/confirm")

  const getCognitiveMediaProps = (constantObj: ValueOf<typeof COGNITIVE_MEDIA>) => {
    return {
      name: "cognitiveMedia",
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value: newValue } = event.target
        setFieldValue("cognitiveMedia", newValue as ValueOf<typeof COGNITIVE_MEDIA>["value"])
      },
      value: constantObj.value,
      checked: seminarEntryForm.cognitiveMedia === constantObj.value,
    }
  }

  const isRequiredAgreedPrivacyPolicy =
    (seminar.personalInfoProvidedTo !== null && seminar.personalInfoProvidedTo !== "") || !isLoggedIn

  return {
    seminar,
    isLoggedIn,
    isRequiredAgreedPrivacyPolicy,
    getFieldProps,
    getPersonalProps(value: string) {
      return {
        name: "personal",
        onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
          const { value: newValue } = event.target
          setFieldValue("personal", newValue as ValueOf<typeof USER_TYPE>["value"])
        },
        value,
        checked: seminarEntryForm.personal === value,
      }
    },
    getRequestContentsProps(constantObj: ValueOf<typeof REQUEST_CONTENTS>) {
      return {
        name: "requestContents",
        onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
          const { value, checked } = event.target
          const nextValues = checked
            ? [...seminarEntryForm.requestContents, value]
            : seminarEntryForm.requestContents.filter((checkedRequest) => checkedRequest !== value)

          setFieldValue("requestContents", nextValues as ValueOf<typeof REQUEST_CONTENTS>["value"][])
        },
        checked: seminarEntryForm.requestContents.includes(constantObj.value),
      }
    },
    getConsultationProps() {
      return {
        name: "consultation",
        onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => {
          const { value: newValue } = event.target
          setFieldValue("consultation", newValue)
        },
        value: seminarEntryForm.consultation,
        disabled: seminarEntryForm.participateIndividualConsultations
          ? !PARTICIPATE_INDIVIDUAL_CONSULTATIONS[seminarEntryForm.participateIndividualConsultations].isParticipate
          : true,
      }
    },
    getParticipateIndividualConsultationsProps(constantObj: ValueOf<typeof PARTICIPATE_INDIVIDUAL_CONSULTATIONS>) {
      return {
        name: "participateIndividualConsultations",
        onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
          const { value: newValue } = event.target
          setFieldValue(
            "participateIndividualConsultations",
            newValue as ValueOf<typeof PARTICIPATE_INDIVIDUAL_CONSULTATIONS>["value"]
          )
        },
        value: constantObj.value,
        checked: seminarEntryForm.participateIndividualConsultations === constantObj.value,
      }
    },
    getCognitiveMediaProps,
    getCognitiveMediaOtherProps() {
      return {
        description: "（その他を選択の場合は詳細をご記入ください）",
        textInputProps: { ...getFieldProps("cognitiveMediaOther"), placeholder: "詳細入力欄" },
        ...getCognitiveMediaProps(COGNITIVE_MEDIA.Other),
      }
    },
    privacyPolicyProps,
    submitButtonProps: {
      disabled: isRequiredAgreedPrivacyPolicy && !privacyPolicyProps.checked,
      onClick: async () => {
        const { canSubmit } = await preSubmit()
        if (canSubmit) {
          router.push({ keepQuery: true })
        }
      },
    },
    getFieldErrors,
  }
}

const SeminarEntryForm = () => {
  const {
    seminar,
    isRequiredAgreedPrivacyPolicy,
    getFieldProps,
    getPersonalProps,
    getRequestContentsProps,
    getParticipateIndividualConsultationsProps,
    getConsultationProps,
    getCognitiveMediaProps,
    getCognitiveMediaOtherProps,
    privacyPolicyProps,
    submitButtonProps,
    getFieldErrors,
    isLoggedIn,
  } = useSeminarEntryForm()

  return (
    <MyPageLayout layout={isLoggedIn ? "full" : "simple"}>
      <SeminarTemplate>
        <SeminarSummary seminar={seminar} />
        <Spacer size={40} />
        <MyPageForm layout="full" fieldWidth={518} gap={30}>
          <MyPageFormField
            label="個人・法人"
            type="required"
            errorMessages={getFieldErrors("personal")}
            alignBaseLine={false}
          >
            <SimpleGrid columns={2}>
              {Object.entries(USER_TYPE).map(([, obj]) => (
                <Radio key={obj.value} {...getPersonalProps(obj.value)}>
                  {obj.label}
                </Radio>
              ))}
            </SimpleGrid>
          </MyPageFormField>
          <MyPageFormField
            label="氏名"
            type="required"
            fieldDescription="（8文字以内、半角カナ不可）"
            asGroup
            errorMessages={getFieldErrors("familyName", "firstName")}
          >
            <div className={styles.userNameContainer}>
              <InputLabel label="姓">
                <TextInput {...getFieldProps("familyName")} placeholder="例：基板" autoComplete="family-name" />
              </InputLabel>
              <InputLabel label="名">
                <TextInput {...getFieldProps("firstName")} placeholder="例：太郎" autoComplete="given-name" />
              </InputLabel>
            </div>
          </MyPageFormField>
          <MyPageFormField
            label="氏名（フリガナ）"
            type="required"
            fieldDescription="（20文字以内、全角カナ）"
            asGroup
            errorMessages={getFieldErrors("familyNameKana", "firstNameKana")}
          >
            <div className={styles.userNameContainer}>
              <InputLabel label="セイ">
                <TextInput {...getFieldProps("familyNameKana")} placeholder="例：キバン" />
              </InputLabel>
              <InputLabel label="メイ">
                <TextInput {...getFieldProps("firstNameKana")} placeholder="例：タロウ" />
              </InputLabel>
            </div>
          </MyPageFormField>
          <MyPageFormField
            label="所属（会社名・学校名）"
            fieldDescription="（法人でお申し込みの場合は入力必須、25文字以内、半角カナ不可）"
            errorMessages={getFieldErrors("company")}
          >
            <TextInput
              {...getFieldProps("company")}
              placeholder="例：株式会社ピーバンドットコム"
              autoComplete="organization"
            />
          </MyPageFormField>
          <MyPageFormField
            label="所属部課名・学部・研究室名"
            fieldDescription="（25文字以内、半角カナ不可）"
            errorMessages={getFieldErrors("department")}
          >
            <TextInput {...getFieldProps("department")} placeholder="例：サポート窓口" />
          </MyPageFormField>
          <MyPageFormField label="メール" type="required" errorMessages={getFieldErrors("email")}>
            <TextInput {...getFieldProps("email")} placeholder="例：sample@pban.com" autoComplete="email" />
          </MyPageFormField>
          <MyPageFormField label="お電話" type="required" errorMessages={getFieldErrors("telNo")}>
            <TextInput
              {...getFieldProps("telNo")}
              placeholder="例：03-3261-3431/0332613431"
              autoComplete="tel-national"
            />
          </MyPageFormField>
          {seminar.seminarCategory === SEMINAR_CATEGORY.IntroductionSeminar.value && (
            <MyPageFormField
              label={"確認したい内容\n（複数回答可）"}
              asGroup
              alignBaseLine={false}
              type="required"
              errorMessages={getFieldErrors("requestContents", "requestMessage")}
            >
              <SimpleGrid columns={2}>
                {Object.entries(REQUEST_CONTENTS).map(([key, obj]) => (
                  <CheckBox key={key} {...getRequestContentsProps(obj)} value={key}>
                    {obj.label}
                  </CheckBox>
                ))}
              </SimpleGrid>
              <Spacer size={22} />
              <TextArea
                {...getFieldProps("requestMessage")}
                placeholder="選択項目以外で、確認したい内容がある場合は詳細をご記入ください"
              />
            </MyPageFormField>
          )}
          {holdIndividualConsultations(seminar.seminarCategory, seminar.onlineSeminar) && (
            <MyPageFormField
              label="個別相談会への参加予定"
              fieldDescription="（個別相談会をご希望の場合は、必ず【参加】をご選択ください）"
              type="required"
              errorMessages={getFieldErrors("participateIndividualConsultations")}
              alignBaseLine={false}
            >
              <SimpleGrid columns={2}>
                {Object.entries(PARTICIPATE_INDIVIDUAL_CONSULTATIONS).map(([key, obj]) => (
                  <Radio key={key} {...getParticipateIndividualConsultationsProps(obj)}>
                    {obj.label}
                  </Radio>
                ))}
              </SimpleGrid>
            </MyPageFormField>
          )}
          {holdIndividualConsultations(seminar.seminarCategory, seminar.onlineSeminar) && (
            <MyPageFormField
              label="個別相談内容"
              fieldDescription="（個別相談会参加の方のみ、任意でご記入ください）"
              asGroup
              alignBaseLine={false}
              errorMessages={getFieldErrors("consultation")}
            >
              <TextArea {...getConsultationProps()} />
            </MyPageFormField>
          )}
          {seminar.seminarCategory !== SEMINAR_CATEGORY.IntroductionSeminar.value && (
            <MyPageFormField
              label="ご質問・ご要望など"
              asGroup
              alignBaseLine={false}
              errorMessages={getFieldErrors("requestContents", "requestMessage")}
            >
              <TextArea {...getFieldProps("requestMessage")} />
            </MyPageFormField>
          )}
          <MyPageFormField
            label="本セミナーを知ったきっかけ"
            type="required"
            errorMessages={getFieldErrors("cognitiveMedia", "cognitiveMediaOther")}
            alignBaseLine={false}
          >
            <SimpleGrid columns={2}>
              {selectableCognitiveMedia(seminar.seminarCategory).map(
                ([key, obj]) =>
                  !obj.isOther && (
                    <Radio key={key} {...getCognitiveMediaProps(obj)}>
                      {obj.label}
                    </Radio>
                  )
              )}
            </SimpleGrid>
            <Spacer size={15} />
            <RadioWithTextInput {...getCognitiveMediaOtherProps()}>その他</RadioWithTextInput>
          </MyPageFormField>
        </MyPageForm>
        <Spacer size={40} />
        {isRequiredAgreedPrivacyPolicy && (
          <ConfirmPrivacyPolicy seminar={seminar}>
            <CheckBox {...privacyPolicyProps} direction="row">
              個人情報の取り扱いについて同意する。
            </CheckBox>
          </ConfirmPrivacyPolicy>
        )}
        <MyPageButtonContainer>
          <Button {...submitButtonProps}>登録内容を確認</Button>
        </MyPageButtonContainer>
      </SeminarTemplate>
    </MyPageLayout>
  )
}

export default SeminarEntryForm

const styles = {
  userNameContainer: css`
    display: flex;
    column-gap: 10px;
  `,
}
